import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import PageHeader from '@components/layout/page-header';
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs.tsx';
import { Globe, Share2, AlertTriangle } from 'lucide-react';
import {
  BreadcrumbItem,
  Breadcrumbs
} from '@components/atoms/ui-kit/breadcrumbs.tsx';
import { PATHS } from '../../router/paths';
import { buildResellerBreadcrumbTrail } from '@utils/breadcrump-trails/build-reseller-breadcrump-trail';
import { useGetLanguageAndConfig } from '@utils/api/back-office/language-and-config/use-get-language-and-config';
import UpdateLanguagePanel from '@components/back-office/language-and-configs/update-language-and-config';
import PublicationsPanel from '@components/back-office/language-and-configs/publications';

type Params = { languageId: string };

export default function LanguageAndConfigurationDetails() {
  const { t } = useTranslation();
  const { languageId: languageIdParam } = useParams<Params>();
  const languageId = Number(languageIdParam || 0);

  const { data, isError } = useGetLanguageAndConfig(languageId);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  useEffect(() => {
    if (languageId)
      buildResellerBreadcrumbTrail(String(languageId)).then(setBreadcrumbs);
  }, [languageId]);

  const tabs: Tab[] = useMemo(
    () => [
      {
        id: 0,
        name: (
          <span className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t('language')}
          </span>
        ),
        component: <UpdateLanguagePanel languageId={languageId} />
      },
      {
        id: 1,
        name: (
          <span className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            {t('publications')}
          </span>
        ),
        component: <PublicationsPanel languageId={languageId} />
      }
    ],
    [languageId, t]
  );

  return (
    <div className="bg-app flex-1 space-y-4 px-4">
      {isError && (
        <div className="flex items-center gap-2 rounded-2xl bg-red-500/10 border border-red-500/25 px-4 py-3 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{t('failed-to-load-data')}</span>
        </div>
      )}
      <PageHeader
        title={
          data?.result ? (
            <Breadcrumbs
              items={breadcrumbs.slice(0, -1)}
              currentFolderName={data.result.name || ''}
              rootLabel="Language & Configuration"
              rootPath={PATHS().LANGUAGE_AND_CONFIGS}
            />
          ) : (
            t('language-and-configuration-details')
          )
        }
        caption={t('manage-lanaguage-details')}
      />
      <Tabs tabs={tabs} searchParams minHeight="80vh" maxHeight="85vh" />
    </div>
  );
}
